import { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { LayoutGrid, Play, FileJson, ListChecks, Sparkles, Check, X, Braces, Sliders } from 'lucide-react';
import { Button, Alert, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { canonicalGraph, canonicalizeGraph } from '@/lib/workflowGraph';
import { autoLayout } from '@/lib/workflowLayout';
import {
  addNode, duplicateNode, deleteNode, updateNodeData, upstreamNodeIds, nodesNamedInError,
} from '@/lib/workflowEdits';
import {
  useWorkflowData, useBuilderCatalogs, useGraphValidation, useWorkflowRuns, useWorkflowActions,
  buildWorkflowContext, errText,
} from '@/lib/workflowBuilderHooks';
import { useChatDock } from '@/lib/ChatDockContext';
import BuilderTopBar from '@/components/workflow/BuilderTopBar';
import NodePalette from '@/components/workflow/NodePalette';
import NodeInspector from '@/components/workflow/NodeInspector';
import InputContractDrawer from '@/components/workflow/InputContractDrawer';
import RunsPanel from '@/components/workflow/RunsPanel';
import SpecPanel from '@/components/workflow/SpecPanel';
import LlmAppSpecPanel from '@/components/workflow/LlmAppSpecPanel';
import TestDialog from '@/components/workflow/TestDialog';
import ApiPanel from '@/components/workflow/ApiPanel';
import PageShell from '@/components/layout/PageShell';

// Lazily loaded: @xyflow/react plus zustand and a dozen d3-* packages is a few hundred KB
// and this page is its only consumer — a dynamic import keeps it out of the initial bundle
// entirely (vite.config.js's manualChunks pins it to its own cacheable `canvas` chunk).
const WorkflowCanvas = lazy(() => import('@/components/WorkflowCanvas'));

/** Same footprint as the canvas it stands in for, so the lazy load never shifts layout. */
function CanvasFallback() {
  return <div className="h-full w-full rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] animate-pulse" />;
}

const NOOP = () => {};

/** Injected-not-authored node data: the run tint, the live lint flag, and the card's hover
 *  actions. Stripped on the way back in so none of it can reach graph_json. */
const INJECTED = ['__onDuplicate', '__onDelete', '__status', '__invalid'];

function stripInjected(nodes) {
  return nodes.map(({ data, ...rest }) => {
    const clean = { ...(data || {}) };
    for (const key of INJECTED) delete clean[key];
    return { ...rest, data: clean };
  });
}

/* --------------------------------------------------------------------- canvas chrome -- */

function CanvasToolbar({
  onAutoLayout, onTest, onToggleSpec, specOpen, onToggleContract, contractOpen, fieldCount, canRun, onAskArchitect,
}) {
  return (
    <div className="absolute top-1.5 right-1.5 z-10 flex items-center gap-1">
      {onAskArchitect && (
        <Button size="sm" variant="secondary" onClick={onAskArchitect} title="Describe a change and let Workflow Architect draft it">
          <Sparkles size={13} className="inline mr-1" />Ask Workflow Architect
        </Button>
      )}
      <Button size="sm" variant="secondary" onClick={onAutoLayout} title="Re-arrange nodes left to right">
        <LayoutGrid size={13} className="inline mr-1" />Auto Layout
      </Button>
      <Button
        size="sm"
        variant={contractOpen ? 'primary' : 'secondary'}
        onClick={onToggleContract}
        title="View and edit input parameters contract"
      >
        <Sliders size={13} className="inline mr-1" />Input Contract ({fieldCount})
      </Button>
      <Button size="sm" variant="secondary" onClick={onTest} disabled={!canRun} title={canRun ? 'Run with an input' : 'Save first — a run executes the stored definition'}>
        <Play size={13} className="inline mr-1" />Test
      </Button>
      <Button size="sm" variant={specOpen ? 'primary' : 'secondary'} onClick={onToggleSpec}>
        <FileJson size={13} className="inline mr-1" />Specification
      </Button>
    </div>
  );
}

const TABS = [['build', 'Build', LayoutGrid], ['llmapp', 'LLM App', Braces], ['runs', 'Results', ListChecks], ['api', 'API', Braces]];

function TabBar({ tab, setTab, hasSpec }) {
  const tabs = TABS.filter(([key]) => key !== 'llmapp' || hasSpec);
  return (
    <div className="flex items-center gap-0.5 border-b border-[var(--app-border)] shrink-0">
      {tabs.map(([key, label, Icon]) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
              active
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300 shadow-xs'
                : 'text-secondary-600 hover:text-[var(--app-text)] hover:bg-[var(--app-surface)]'
            }`}
          >
            <Icon size={14} className={active ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-400'} />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------------- tabs -- */

function BuildTabSidePanel({
  panels, inputFields, setFields, spec, selected, edits, catalogs,
}) {
  if (panels.contractOpen) {
    return (
      <InputContractDrawer
        fields={inputFields}
        setFields={setFields}
        onClose={panels.toggleContract}
      />
    );
  }
  if (panels.specOpen) {
    return (
      <SpecPanel
        spec={spec.graph}
        validation={spec.validation}
        validating={spec.validating}
        onClose={panels.toggleSpec}
      />
    );
  }
  return (
    <NodeInspector
      node={selected}
      onChangeData={edits.changeData}
      onDelete={() => edits.remove(selected.id)}
      opts={catalogs.inspectorOpts}
      collapsed={panels.inspectorCollapsed}
      onToggleCollapsed={panels.toggleInspector}
    />
  );
}

function BuildTab({
  graph, setGraph, selected, edits, panels, catalogs, spec, agents, inputFields, setFields,
}) {
  return (
    <div className="flex gap-2 flex-1 min-h-0">
      <NodePalette
        nodeCatalog={catalogs.nodeCatalog}
        toolOptions={catalogs.toolOptions}
        providers={catalogs.providers}
        connections={catalogs.connections}
        modelsByProvider={catalogs.modelsByProvider}
        agents={agents}
        connectors={catalogs.connectors}
        onAdd={(entry) => edits.add({ nodeType: entry.nodeType, data: entry.data })}
        collapsed={panels.paletteCollapsed}
        onToggleCollapsed={panels.togglePalette}
      />

      <div className="relative flex-1 min-w-0">
        <CanvasToolbar
          onAutoLayout={() => setGraph((g) => ({ ...g, nodes: autoLayout(g.nodes, g.edges) }))}
          onTest={panels.openTest}
          onToggleSpec={panels.toggleSpec}
          specOpen={panels.specOpen}
          onToggleContract={panels.toggleContract}
          contractOpen={panels.contractOpen}
          fieldCount={inputFields?.length || 0}
          canRun={panels.canRun}
          onAskArchitect={panels.askArchitect}
        />
        <Suspense fallback={<CanvasFallback />}>
          <WorkflowCanvas
            nodes={edits.decoratedNodes}
            edges={graph.edges}
            onNodesChange={(nodes) => setGraph((g) => ({ ...g, nodes: stripInjected(nodes) }))}
            onEdgesChange={(edges) => setGraph((g) => ({ ...g, edges }))}
            onSelectNode={edits.select}
            onDropNode={edits.add}
            selectedId={selected?.id || null}
          />
        </Suspense>
      </div>

      <BuildTabSidePanel
        panels={panels}
        inputFields={inputFields}
        setFields={setFields}
        spec={spec}
        selected={selected}
        edits={edits}
        catalogs={catalogs}
      />
    </div>
  );
}

function RunsTab({ displayNodes, edges, runs }) {
  return (
    <div className="flex flex-col h-full gap-2">
      <div className="flex-1 min-h-0">
        <Suspense fallback={<CanvasFallback />}>
          <WorkflowCanvas
            nodes={displayNodes}
            edges={edges}
            onNodesChange={NOOP}
            onEdgesChange={NOOP}
            onSelectNode={NOOP}
            selectedId={null}
            readOnly
          />
        </Suspense>
      </div>
      <div className="flex-[1.1] min-h-0">
        <RunsPanel {...runs} onSelectRun={runs.openRun} />
      </div>
    </div>
  );
}

/** The three-way tab switch, extracted because the page function was already at its
 *  complexity ceiling — each pane owns its props, the page just routes. */
function TabBody({ tab, form, agents, inputFields, setState, graph, setGraph, selected, edits, panels, catalogs, spec, runDisplayNodes, runs, onSpecSaved }) {
  if (tab === 'build') {
    return (
      <BuildTab
        graph={graph}
        setGraph={setGraph}
        selected={selected}
        edits={edits}
        panels={panels}
        catalogs={catalogs}
        spec={spec}
        agents={agents}
        inputFields={inputFields}
        setFields={(next) => setState((s) => ({ ...s, fields: next }))}
      />
    );
  }
  if (tab === 'api') {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <ApiPanel form={form} fields={inputFields} graph={graph} />
      </div>
    );
  }
  if (tab === 'llmapp') {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto">
        <LlmAppSpecPanel
          form={form}
          agents={agents || []}
          onSaved={onSpecSaved}
        />
      </div>
    );
  }
  return <RunsTab displayNodes={runDisplayNodes} edges={graph.edges} runs={runs} />;
}

/* --------------------------------------------------------------------------- state -- */

/** Which side panels are open, and whether Run is currently allowed. */
function usePanels({ dirty, onTest, onAskArchitect }) {
  const [paletteCollapsed, setPaletteCollapsed] = useState(false);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  return {
    paletteCollapsed,
    togglePalette: () => setPaletteCollapsed((v) => !v),
    inspectorCollapsed,
    toggleInspector: () => setInspectorCollapsed((v) => !v),
    specOpen,
    toggleSpec: () => {
      setSpecOpen((v) => {
        if (!v) setContractOpen(false);
        return !v;
      });
    },
    contractOpen,
    toggleContract: () => {
      setContractOpen((v) => {
        if (!v) setSpecOpen(false);
        return !v;
      });
    },
    openTest: onTest,
    askArchitect: onAskArchitect,
    canRun: !dirty,
  };
}

/**
 * The card's hover actions, hung off node.data.
 *
 * Module-level so the arrows nest one level deep rather than four, and so the closure is
 * over the passed handlers rather than over whatever `graph` happened to be in scope.
 */
function decorateNode(node, { onDuplicate, onDelete, invalidIds, invalidMessage }) {
  return {
    ...node,
    data: {
      ...node.data,
      __onDuplicate: () => onDuplicate(node.id),
      __onDelete: () => onDelete(node.id),
      __invalid: invalidIds.has(node.id) ? invalidMessage : undefined,
    },
  };
}

/** The live-lint arguments. Split out of the component so the null-guards on `form` (which
 *  is absent until the row loads) do not count against its complexity budget. `agentKey`
 *  matters to the lint: the server applies the same unbound-Tool-node rule the save does. */
function lintArgs(fetchApi, graph, form, tab) {
  return {
    fetchApi,
    graph,
    enabled: tab === 'build' && !!form,
  };
}

/** What the inspector's dropdowns are filled from. `upstreamIds` is per-selection, which is
 *  why this is derived here rather than inside useBuilderCatalogs. `agents` feeds the
 *  per-node specialist pickers (tool override, Agent Turn), and `fetchAgentToolOptions`
 *  resolves one agent's toolset for the Tool picker without disturbing the workflow's. */
function inspectorOpts(catalogs, graph, selected, { agents, fetchApi, form }) {
  return {
    providers: catalogs.providers,
    connections: catalogs.aiConnections,
    imageConnections: catalogs.imageConnections,
    toolOptions: catalogs.toolOptions,
    transformCatalog: catalogs.transformCatalog || [],
    upstreamIds: selected ? upstreamNodeIds(graph, selected.id) : [],
    agents: agents || [],
    // Sub-workflow / For Each pickers, minus this workflow itself: the engine refuses a
    // direct self-call, so offering it would only build a graph that fails at run time.
    workflows: catalogs.workflows || [],
    currentWorkflowKey: (form && form.key) || null,
    // The agent a Test step resolves tools against when the node names none — the same
    // fallback walkRun uses for a real run.
    workflowAgentKey: (form && form.agent_key) || null,
    fetchAgentToolOptions: (agentKey) => fetchApi('workflowToolOptions', agentKey).then((res) => res?.tools || []),
    runTestNode: (body) => fetchApi('workflowTestNode', body),
  };
}

/* ------------------------------------------------------------ workflow architect -- */

const ARCHITECT_KEY = 'workflow_architect';

/**
 * The two-way link to Workflow Architect in the floating dock.
 *
 * Out: what this page is showing, registered as a getter so the dock reads the CURRENT
 * graph at send time rather than whatever it was when the dock last rendered.
 * In: a `propose_workflow_graph` result the dock lifted out of a finished turn's traces.
 */
function useArchitect({ form, graph, fields, toolOptions }) {
  const dock = useChatDock();
  const { registerScreenContext, openDockWithAgent, proposal, clearProposal } = dock || {};

  // Live values through a ref, so the registration itself never has to be re-run (and the
  // dock never re-rendered) just because a node moved.
  const latest = useRef({ form, graph, fields, toolOptions });
  latest.current = { form, graph, fields, toolOptions };

  useEffect(() => {
    if (!registerScreenContext) return undefined;
    return registerScreenContext(() => buildWorkflowContext(latest.current));
  }, [registerScreenContext]);

  return {
    ask: openDockWithAgent ? () => openDockWithAgent(ARCHITECT_KEY) : null,
    proposal: proposal && proposal.tool === 'propose_workflow_graph' ? proposal : null,
    clearProposal,
  };
}

/** Offers a drafted graph rather than applying it. Nothing the agent produces reaches the
 *  canvas without this click, and nothing reaches the database without a subsequent Save. */
function ProposalBanner({ proposal, dirty, onApply, onDismiss }) {
  const summary = proposal.output?.summary;
  const ok = proposal.output?.ok && proposal.output?.proposal;
  return (
    <Alert variant={ok ? 'info' : 'warning'} title="Workflow Architect has a draft">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm">
          {ok ? (summary || 'A new graph is ready to review on the canvas.') : (proposal.output?.error || 'The draft was not valid.')}
        </span>
        {dirty && ok && (
          <span className="text-xs text-amber-700">Applying replaces your unsaved changes.</span>
        )}
        <div className="flex-1" />
        {ok && (
          <Button size="sm" onClick={onApply}>
            <Check size={13} className="inline mr-1" />{dirty ? 'Replace canvas' : 'Apply to canvas'}
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={onDismiss}>
          <X size={13} className="inline mr-1" />Dismiss
        </Button>
      </div>
    </Alert>
  );
}

/** Everything the palette, canvas and inspector do to the graph, plus the decorated node
 *  list the canvas renders. Its own hook so the page body stays layout and wiring. */
function useGraphEdits({ graph, setGraph, setState, selectedId, setSelectedId, lintError }) {
  const removeNode = useCallback((nodeId) => {
    setGraph((g) => deleteNode(g, nodeId));
    setSelectedId((cur) => (cur === nodeId ? null : cur));
  }, [setGraph, setSelectedId]);
  const copyNode = useCallback((nodeId) => setGraph((g) => duplicateNode(g, nodeId).graph), [setGraph]);
  const cardHandlers = useMemo(() => ({
    onDuplicate: copyNode,
    onDelete: removeNode,
    invalidIds: nodesNamedInError(graph.nodes, lintError),
    invalidMessage: lintError,
  }), [copyNode, removeNode, graph.nodes, lintError]);

  // Derived for display only — never written into `graph` state, so hovering a card
  // cannot mark the form dirty.
  const decoratedNodes = useMemo(
    () => graph.nodes.map((n) => decorateNode(n, cardHandlers)),
    [graph.nodes, cardHandlers],
  );

  return {
    decoratedNodes,
    select: setSelectedId,
    add: (entry) => setState((s) => {
      const { graph: next, id: newId } = addNode(s.graph, entry);
      setSelectedId(newId);
      return { ...s, graph: next };
    }),
    remove: removeNode,
    changeData: (data) => setGraph((g) => updateNodeData(g, selectedId, data)),
  };
}

/** Lands a drafted graph as UNSAVED editor state — the existing dirty tracking, live lint
 *  and Save all still apply, so an agent can never write to the row behind the user. */
function applyProposalToCanvas(architect, { setGraph, setSelectedId, setNotice }) {
  const next = architect.proposal?.output?.proposal;
  if (!next) return;
  setGraph({ nodes: autoLayout(next.nodes || [], next.edges || []), edges: next.edges || [] });
  setSelectedId(null);
  architect.clearProposal();
  setNotice("Applied Workflow Architect's draft — review it and Save.");
}

/* ---------------------------------------------------------------------------- page -- */

export default function WorkflowDetail() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();

  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState('build');
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [testOpen, setTestOpen] = useState(false);
  // Bumped after an LLM App spec save: the spec recompiles the graph server-side, so the
  // loaded row (canvas included) must be refetched to show what is actually stored.
  const [specReloadKey, setSpecReloadKey] = useState(0);

  const { state, setState, pristine, setPristine, agents, loading } = useWorkflowData({
    id, fetchApi, onError: setErr, reloadKey: specReloadKey,
  });
  const { form, graph, fields: inputFields } = state;
  const catalogs = useBuilderCatalogs({ fetchApi, agentKey: form?.agent_key });
  const runs = useWorkflowRuns({ id, tab, fetchApi, onError: setErr });
  const actions = useWorkflowActions({ id, fetchApi, state, setState, setPristine, setErr, setNotice });

  const setGraph = useCallback((updater) => setState((s) => ({
    ...s, graph: typeof updater === 'function' ? updater(s.graph) : updater,
  })), []);
  const setField = (key, value) => setState((s) => ({ ...s, form: { ...s.form, [key]: value } }));
  const selected = useMemo(() => graph.nodes.find((n) => n.id === selectedId) || null, [graph.nodes, selectedId]);

  // The graph half MUST go through the canonicalizer: reactflow mutates node objects on
  // every drag and select (selected/dragging/measured/positionAbsolute, and continuously
  // rewritten float positions), so a plain stringify compare marks the form dirty on a pan.
  const dirty = useMemo(() => {
    if (!form || !pristine.form) return false;
    return JSON.stringify(form) !== JSON.stringify(pristine.form)
      || canonicalizeGraph(graph.nodes, graph.edges) !== canonicalizeGraph(pristine.graph.nodes, pristine.graph.edges)
      || JSON.stringify(inputFields) !== JSON.stringify(pristine.fields);
  }, [form, graph, inputFields, pristine]);

  const architect = useArchitect({ form, graph, fields: inputFields, toolOptions: catalogs.toolOptions });
  const panels = usePanels({ dirty, onTest: () => setTestOpen(true), onAskArchitect: architect.ask });

  const applyProposal = () => applyProposalToCanvas(architect, { setGraph, setSelectedId, setNotice });
  const canonical = useMemo(() => canonicalGraph(graph.nodes, graph.edges), [graph]);
  // Linted continuously while building, not only when the Specification panel is open: the
  // whole point is to surface a cycle or an unreachable node BEFORE the user hits Save and
  // reads it as a 400. Gated on !!form — before the row loads the canvas is EMPTY by
  // definition, and linting it produces only the misleading "needs at least one node" 400.
  const spec = { graph: canonical, ...useGraphValidation(lintArgs(fetchApi, canonical, form, tab)) };
  const lintError = spec.validation && !spec.validation.ok ? spec.validation.error : null;

  const edits = useGraphEdits({ graph, setGraph, setState, selectedId, setSelectedId, lintError });

  const runDisplayNodes = useMemo(() => {
    if (tab !== 'runs' || !runs.steps.length) return graph.nodes;
    const byNode = new Map(runs.steps.map((s) => [s.node_id, s.status]));
    return graph.nodes.map((n) => ({ ...n, data: { ...n.data, __status: byNode.get(n.id) } }));
  }, [graph.nodes, runs.steps, tab]);

  const runNow = async (input = {}) => {
    setErr('');
    setNotice('');
    const { runId } = await fetchApi('workflowRun', { id, input });
    setTestOpen(false);
    setNotice(`Started run #${runId}.`);
    setTab('runs');
    await runs.loadRuns();
    await runs.openRun(runId);
  };

  if (loading) return <PageShell><Loader text="Loading workflow…" /></PageShell>;
  if (!form) return <PageShell><Alert variant="error">{err || 'Workflow not found'}</Alert></PageShell>;

  return (
    // Full bleed: a graph canvas cannot size itself from its content, so it takes the
    // height the shell hands down. Everything above the workspace is shrink-0, which is
    // what makes a banner appearing shrink the canvas instead of pushing the page.
    <PageShell bleed className="gap-2 p-2">
      <div className="shrink-0 space-y-2">
        <BuilderTopBar
          form={form}
          setField={setField}
          agents={agents}
          dirty={dirty}
          saving={actions.saving}
          publishing={actions.publishing}
          onSave={actions.save}
          onRun={() => setTestOpen(true)}
          onTogglePublished={actions.togglePublished}
        />

        {err && <Alert variant="error">{err}</Alert>}
        {notice && <Alert variant="success">{notice}</Alert>}
        {tab === 'build' && architect.proposal && (
          <ProposalBanner
            proposal={architect.proposal}
            dirty={dirty}
            onApply={applyProposal}
            onDismiss={architect.clearProposal}
          />
        )}
        {tab === 'build' && lintError && (
          <Alert variant="warning" title="This graph will not save yet">{lintError}</Alert>
        )}

        <TabBar tab={tab} setTab={setTab} hasSpec={!!form.spec_json} />
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        <TabBody
          tab={tab}
          form={form}
          agents={agents}
          inputFields={inputFields}
          setState={setState}
          graph={graph}
          setGraph={setGraph}
          selected={selected}
          edits={edits}
          panels={panels}
          catalogs={{ ...catalogs, inspectorOpts: inspectorOpts(catalogs, graph, selected, { agents, fetchApi, form }) }}
          spec={spec}
          runDisplayNodes={runDisplayNodes}
          runs={runs}
          onSpecSaved={() => setSpecReloadKey((k) => k + 1)}
        />
      </div>

      {testOpen && (
        <TestDialog
          fields={inputFields}
          onClose={() => setTestOpen(false)}
          onRun={(input) => runNow(input).catch((ex) => { setErr(errText(ex)); throw ex; })}
        />
      )}
    </PageShell>
  );
}
